import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { AgeVerification } from "@/components/AgeVerification";
import Home from "@/pages/home";
import About from "@/pages/about";
import BenCare from "@/pages/ben-care";
import FormulationJourney from "@/pages/articles/formulation-journey";
import LabResults from "@/pages/lab-results";
import Terms from "@/pages/terms";
import NotFound from "@/pages/not-found";
import Legal from "@/pages/legal";
import { useState, useEffect } from "react";

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

function Router() {
  return (
    <Switch>
      <Route path="/about">
        <About />
      </Route>
      <Route path="/ben-care">
        <BenCare />
      </Route>
      <Route path="/articles/first-light-to-golden-hour-liquid-stretch">
        <FormulationJourney />
      </Route>
      <Route path="/articles/60-formulations-3-1-cbd-thc-liquid-stretch">
        <FormulationJourney />
      </Route>
      <Route path="/lab-results">
        <LabResults />
      </Route>
      <Route path="/terms">
        <Terms />
      </Route>
      <Route path="/legal">
        <Legal />
      </Route>
      <Route path="/" component={Home} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  const [showButton, setShowButton] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [location] = useLocation();
  const isHomePage = location === "/";

  useEffect(() => {
    fetch(`/deploy.txt?v=${Date.now()}`, { cache: "no-store" })
      .then((r) => r.text())
      .then((txt) => console.log("[DEPLOY]\n" + txt))
      .catch((e) => console.log("[DEPLOY] unable to read /deploy.txt", e));
  }, []);

  // GA4 SPA pageview tracking (gtag.js)
  useEffect(() => {
    if (typeof window.gtag !== "function") return;

    window.gtag("event", "page_view", {
      page_path: location,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location]);

  // Track when user navigates to non-homepage routes
  useEffect(() => {
    if (!isHomePage) {
      // User has navigated to a non-homepage route
      sessionStorage.setItem('hasNavigatedWithinSite', 'true');
    }
  }, [location, isHomePage]);

  useEffect(() => {
    const handleScroll = () => {
      // Don't show button on homepage
      if (isHomePage) {
        setShowButton(false);
        return;
      }

      // Show button on desktop always
      if (window.innerWidth >= 768) {
        setShowButton(true);
        return;
      }
      
      // On mobile, show button when scrolling up or when at bottom of page
      const currentScrollY = window.scrollY;
      const isScrollingUp = currentScrollY < lastScrollY;
      const isNearBottom = 
        window.innerHeight + currentScrollY >= 
        document.documentElement.scrollHeight - 100;
      
      // Always show when near the bottom or when scrolling up
      if (isNearBottom || isScrollingUp || currentScrollY < 100) {
        setShowButton(true);
      } else {
        setShowButton(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Call once to set initial state
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, isHomePage]);

  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow mt-[calc(4rem+2rem)]">
          <Router />
        </main>
        {!isHomePage && <Footer variant="brand" />}
      </div>
      <AgeVerification isHomePage={isHomePage} />
      <Toaster />
    </QueryClientProvider>
  );
}

export default App;
